import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  TableSortLabel,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import ColumnVisibilityMenu from './ColumnVisibilityMenu';
import { PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE } from '../../utils/constants';
import { useTranslations } from '../../hooks/use-translations';

// Custom sorting function for date/datetime columns (outside component to prevent recreation)
const dateSortingFn = (rowA, rowB, columnId) => {
  const valueA = rowA.getValue(columnId);
  const valueB = rowB.getValue(columnId);
  
  // Handle null/undefined values
  if (!valueA && !valueB) return 0;
  if (!valueA) return 1;
  if (!valueB) return -1;
  
  // Try to parse dates - handle various formats
  let dateA, dateB;
  
  // Check if it's already a Date object
  if (valueA instanceof Date) {
    dateA = valueA;
  } else if (typeof valueA === 'string') {
    // Try to parse DD.MM.YYYY format (common in EU)
    const ddmmyyyyMatch = valueA.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
    if (ddmmyyyyMatch) {
      const [, day, month, year] = ddmmyyyyMatch;
      // Extract time if present
      const timeMatch = valueA.match(/(\d{2}):(\d{2}):(\d{2})/);
      if (timeMatch) {
        const [, hour, minute, second] = timeMatch;
        dateA = new Date(year, month - 1, day, hour, minute, second);
      } else {
        dateA = new Date(year, month - 1, day);
      }
    } else {
      // Fallback to standard Date parsing
      dateA = new Date(valueA);
    }
  } else {
    dateA = new Date(valueA);
  }
  
  // Same for valueB
  if (valueB instanceof Date) {
    dateB = valueB;
  } else if (typeof valueB === 'string') {
    const ddmmyyyyMatch = valueB.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
    if (ddmmyyyyMatch) {
      const [, day, month, year] = ddmmyyyyMatch;
      const timeMatch = valueB.match(/(\d{2}):(\d{2}):(\d{2})/);
      if (timeMatch) {
        const [, hour, minute, second] = timeMatch;
        dateB = new Date(year, month - 1, day, hour, minute, second);
      } else {
        dateB = new Date(year, month - 1, day);
      }
    } else {
      dateB = new Date(valueB);
    }
  } else {
    dateB = new Date(valueB);
  }
  
  // Handle invalid dates
  if (isNaN(dateA.getTime())) return 1;
  if (isNaN(dateB.getTime())) return -1;
  
  return dateA.getTime() - dateB.getTime();
};

const ResizableDataGrid = ({
  rows,
  columns,
  getRowId,
  disableRowSelectionOnClick = true,
  sx = {},
  tableFrameSx = {},
  autoHeight = false,
  initialState = {},
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  onCellClick,
  slots,
  sortModel,
  onSortModelChange,
  rowHeight,
  getRowHeight,
  enableColumnVisibility = true,
  storageKey,
  serverPagination, // Optional: { page, pageSize, totalCount, onPageChange, onPageSizeChange }
  manualSorting: manualSortingProp = false, // When true, disables client-side sorting; parent handles via onSortModelChange
  totalRowCount, // Optional: override the total count display in pagination (useful for cursor-based pagination)
  disablePaginationButtons = false, // Disable page navigation buttons (prev/next)
  expandedRowId, // Optional: ID of the currently expanded row
  onRowExpand, // Optional: callback when a row is clicked to expand/collapse (rowId) => void
  getExpandedRowContent, // Optional: function to render expanded content (row) => React.Node
  isRowExpandable, // Optional: (row) => boolean — when set, only matching rows expand
  getRowClassName, // Optional: callback for row class names ({ row }) => string
  renderRowCorner, // Optional: ({ row }) => React.Node — top-left corner adornment
  columnVisibilityButtonProps,
  ...otherProps
}) => {
  const isServerPagination = !!serverPagination;
  const { t } = useTranslations();

  // Convert MUI DataGrid column format to TanStack Table format
  const tanstackColumns = useMemo(
    () =>
      columns.map((col) => {
        const columnDef = {
          id: col.field,
          accessorKey: col.field,
          header:
            typeof col.renderHeader === 'function'
              ? () => col.renderHeader({ field: col.field, colDef: col })
              : (col.headerName ?? col.field),
          size: col.width || undefined,
          minSize: col.minWidth || 50,
          maxSize: col.maxWidth || 1000,
          enableResizing: col.resizable !== false,
          enableSorting: col.sortable !== false,
          enableHiding: col.hideable !== false && col.field !== 'action' && col.field !== 'actions' && col.field !== 'mobileView',
          cell: col.renderCell
            ? ({ row, getValue }) => col.renderCell({ row: row.original, value: getValue() })
            : ({ getValue }) => getValue(),
        };

        // Add sorting function for date/datetime columns
        if (col.type === 'date' || col.type === 'dateTime') {
          columnDef.sortingFn = dateSortingFn;
        } else if (col.sortingFn) {
          columnDef.sortingFn = col.sortingFn;
        }

        return columnDef;
      }),
    [columns]
  );

  const hideTableHeader = useMemo(
    () => columns.length > 0 && columns.every((col) => col.field === 'mobileView' || col.headerName === ''),
    [columns]
  );

  // Convert sortModel to TanStack format
  const sorting = useMemo(() => {
    if (sortModel && sortModel.length > 0) {
      return sortModel.map((s) => ({
        id: s.field,
        desc: s.sort === 'desc',
      }));
    }
    if (initialState?.sorting?.sortModel) {
      return initialState.sorting.sortModel.map((s) => ({
        id: s.field,
        desc: s.sort === 'desc',
      }));
    }
    return [];
  }, [sortModel, initialState]);

  // Load column visibility from localStorage and handle columns with hide: true
  const getInitialColumnVisibility = () => {
    // First, build default visibility from column definitions (hide: true columns should be hidden)
    const defaultVisibility = {};
    columns.forEach((col) => {
      if (col.hide === true) {
        defaultVisibility[col.field] = false;
      }
    });

    if (storageKey && enableColumnVisibility) {
      try {
        const stored = localStorage.getItem(`dataGrid_${storageKey}_visibility`);
        // Merge stored visibility with default (stored takes precedence except for hide: true columns)
        return stored ? { ...defaultVisibility, ...JSON.parse(stored) } : defaultVisibility;
      } catch (error) {
        console.error('Failed to load column visibility from localStorage:', error);
        return defaultVisibility;
      }
    }
    return defaultVisibility;
  };

  const [columnResizing, setColumnResizing] = React.useState({});
  const [sortingState, setSortingState] = React.useState(sorting);
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: initialState?.pagination?.pageSize || DEFAULT_PAGE_SIZE,
  });
  const [columnVisibility, setColumnVisibility] = React.useState(getInitialColumnVisibility());
  const localRowCount = totalRowCount ?? rows.length;
  const localMaxPageIndex = Math.max(0, Math.ceil(localRowCount / pagination.pageSize) - 1);
  const safeLocalPageIndex = Math.min(localMaxPageIndex, Math.max(0, pagination.pageIndex || 0));

  // Update sorting when external sortModel changes
  React.useEffect(() => {
    if (sortModel) {
      setSortingState(
        sortModel.map((s) => ({
          id: s.field,
          desc: s.sort === 'desc',
        }))
      );
    }
  }, [sortModel]);

  // Save column visibility to localStorage
  React.useEffect(() => {
    if (storageKey && enableColumnVisibility) {
      try {
        localStorage.setItem(
          `dataGrid_${storageKey}_visibility`,
          JSON.stringify(columnVisibility)
        );
      } catch (error) {
        console.error('Failed to save column visibility to localStorage:', error);
      }
    }
  }, [columnVisibility, storageKey, enableColumnVisibility]);

  const table = useReactTable({
    data: rows,
    columns: tanstackColumns,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    ...((isServerPagination || manualSortingProp) ? {} : { getSortedRowModel: getSortedRowModel() }),
    // Only use client-side pagination if not using server pagination
    ...(isServerPagination ? {} : { getPaginationRowModel: getPaginationRowModel() }),
    manualPagination: isServerPagination,
    manualSorting: isServerPagination || manualSortingProp,
    pageCount: isServerPagination ? Math.ceil(serverPagination.totalCount / serverPagination.pageSize) : undefined,
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sortingState) : updater;
      setSortingState(newSorting);
      if (onSortModelChange) {
        onSortModelChange(
          newSorting.map((s) => ({
            field: s.id,
            sort: s.desc ? 'desc' : 'asc',
          }))
        );
      }
    },
    onPaginationChange: (updater) => {
      if (isServerPagination) {
        setPagination(updater);
        return;
      }

      setPagination((current) => {
        const next = typeof updater === 'function' ? updater(current) : updater;
        const nextPageSize = Number(next?.pageSize) || current.pageSize;
        const nextPageIndexRaw = Number(next?.pageIndex);
        const nextPageIndex = Number.isFinite(nextPageIndexRaw) ? nextPageIndexRaw : current.pageIndex;
        const maxPageIndex = Math.max(0, Math.ceil(((totalRowCount ?? rows.length) || 0) / nextPageSize) - 1);

        return {
          pageSize: nextPageSize,
          pageIndex: Math.min(maxPageIndex, Math.max(0, nextPageIndex)),
        };
      });
    },
    onColumnSizingChange: setColumnResizing,
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      sorting: sortingState,
      pagination: isServerPagination 
        ? { pageIndex: serverPagination.page - 1, pageSize: serverPagination.pageSize }
        : { pageIndex: safeLocalPageIndex, pageSize: pagination.pageSize },
      columnSizing: columnResizing,
      columnVisibility,
    },
    getRowId: getRowId || ((row, index) => row.id ?? index),
  });

  const handleChangePage = (event, newPage) => {
    const parsedPage = Number(newPage);
    const requestedPage = Number.isFinite(parsedPage) ? parsedPage : 0;

    if (isServerPagination && serverPagination.onPageChange) {
      const maxPage = Math.max(0, Math.ceil(serverPagination.totalCount / serverPagination.pageSize) - 1);
      const safePage = Math.min(maxPage, Math.max(0, requestedPage));
      // Server pagination uses 1-based page numbers
      serverPagination.onPageChange(safePage + 1);
    } else {
      const maxPage = Math.max(0, table.getPageCount() - 1);
      const safePage = Math.min(maxPage, Math.max(0, requestedPage));
      table.setPageIndex(safePage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newPageSize = parseInt(event.target.value, 10);
    if (isServerPagination && serverPagination.onPageSizeChange) {
      serverPagination.onPageSizeChange(newPageSize);
    } else {
      table.setPageSize(newPageSize);
    }
  };

  const hasTableFrame = Object.keys(tableFrameSx || {}).length > 0;
  const defaultSx = {
    backgroundColor: 'white',
    borderRadius: 2,
    px: 3,
    mt: 3,
    minWidth: 0,
    maxWidth: '100%',
    ...sx,
    ...(hasTableFrame ? { backgroundColor: 'transparent', px: 0, borderRadius: 0 } : {}),
  };
  const toolbarSx = {
    p: 2,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    ...(hasTableFrame ? { px: 0, pt: 0, pb: 1.5 } : {}),
  };

  const ToolbarComponent = slots?.toolbar;
  const paginationSlotProps = {
    select: {
      inputProps: { 'aria-label': 'Rows per page' },
    },
    ...(disablePaginationButtons
      ? {
          actions: {
            previousButton: { disabled: true },
            nextButton: { disabled: true },
          },
        }
      : {}),
  };

  return (
    <Paper sx={defaultSx} elevation={0}>
      {ToolbarComponent && enableColumnVisibility ? (
        <Box sx={{ ...toolbarSx, justifyContent: 'space-between' }}>
          <ToolbarComponent table={table} columns={columns} />
          <ColumnVisibilityMenu
            table={table}
            columns={columns}
            buttonProps={columnVisibilityButtonProps}
          />
        </Box>
      ) : ToolbarComponent ? (
        <Box sx={{ ...toolbarSx, justifyContent: 'flex-end' }}>
          <ToolbarComponent table={table} columns={columns} />
        </Box>
      ) : enableColumnVisibility ? (
        <Box sx={{ ...toolbarSx, justifyContent: 'flex-end' }}>
          <ColumnVisibilityMenu
            table={table}
            columns={columns}
            buttonProps={columnVisibilityButtonProps}
          />
        </Box>
      ) : null}
      <Box sx={{ minWidth: 0, maxWidth: '100%', width: '100%', ...tableFrameSx }}>
        <TableContainer
          sx={{
            maxHeight: autoHeight ? undefined : '100%',
            width: '100%',
            overflowX: hideTableHeader ? 'hidden' : 'auto',
          }}
        >
          <Table
            stickyHeader={!hideTableHeader}
            sx={{
              width: '100%',
              ...(hideTableHeader ? { tableLayout: 'fixed' } : {}),
            }}
          >
            {!hideTableHeader && (
              <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableCell
                      key={header.id}
                      sx={{
                        position: 'relative',
                        width: header.getSize(),
                        minWidth: header.column.columnDef.minSize,
                        backgroundColor: (theme) => theme.palette.grey[100],
                        fontWeight: 600,
                        borderBottom: 2,
                        borderColor: 'divider',
                        userSelect: 'none',
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: header.column.getCanSort() ? 'pointer' : 'default',
                          }}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {header.column.getCanSort() ? (
                            <TableSortLabel
                              active={header.column.getIsSorted() !== false}
                              direction={header.column.getIsSorted() || 'asc'}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {header.column.getIsSorted() ? (
                                <Box component="span" sx={visuallyHidden}>
                                  {header.column.getIsSorted() === 'desc'
                                    ? t('dataGrid.sortedDescending')
                                    : t('dataGrid.sortedAscending')}
                                </Box>
                              ) : null}
                            </TableSortLabel>
                          ) : (
                            flexRender(header.column.columnDef.header, header.getContext())
                          )}
                        </Box>
                      )}
                      {/* Resize Handle */}
                      {header.column.getCanResize() && (
                        <Box
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          sx={{
                            position: 'absolute',
                            right: 0,
                            top: 0,
                            height: '100%',
                            width: '8px',
                            cursor: 'col-resize',
                            userSelect: 'none',
                            touchAction: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 1,
                            '&::after': {
                              content: '""',
                              width: '1px',
                              height: '100%',
                              backgroundColor: (theme) => theme.palette.divider,
                              transition: 'all 0.2s ease',
                            },
                            '&:hover::after': {
                              width: '3px',
                              backgroundColor: 'primary.main',
                            },
                            ...(header.column.getIsResizing() && {
                              '&::after': {
                                width: '3px',
                                backgroundColor: 'primary.main',
                              },
                            }),
                          }}
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              </TableHead>
            )}
            <TableBody>
              {table.getRowModel().rows.map((row) => {
                const isExpanded = expandedRowId === row.original.id;
                const canExpand = typeof onRowExpand === 'function'
                  && (typeof isRowExpandable !== 'function' || isRowExpandable(row.original));
                const rowClassName = getRowClassName ? getRowClassName({ row: row.original }) : undefined;
                return (
                  <React.Fragment key={row.id}>
                    <TableRow
                      className={rowClassName}
                      hover
                      sx={{
                        height: rowHeight || (getRowHeight ? getRowHeight() : undefined),
                        cursor: onCellClick || canExpand ? 'pointer' : 'default',
                        ...(isExpanded && {
                          backgroundColor: (theme) => theme.palette.action.selected,
                        }),
                      }}
                      onClick={() => {
                        if (canExpand) {
                          onRowExpand(row.original.id);
                        }
                      }}
                    >
                      {row.getVisibleCells().map((cell, cellIndex) => {
                        const rowCorner = cellIndex === 0 && renderRowCorner ? renderRowCorner(row.original) : null;

                        return (
                        <TableCell
                          key={cell.id}
                          sx={{
                            width: hideTableHeader ? '100%' : cell.column.getSize(),
                            minWidth: hideTableHeader ? 0 : cell.column.columnDef.minSize,
                            position: cellIndex === 0 ? 'relative' : undefined,
                            overflow: hideTableHeader ? 'hidden' : 'visible',
                            ...(rowCorner ? { pt: 3 } : {}),
                          }}
                          onClick={(e) => {
                            if (onCellClick) {
                              e.stopPropagation();
                              onCellClick({ row: row.original, value: cell.getValue(), field: cell.column.id });
                            }
                          }}
                        >
                          {rowCorner && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 4,
                                left: 4,
                                zIndex: 2,
                                width: 'max-content',
                              }}
                            >
                              {rowCorner}
                            </Box>
                          )}
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      );
                      })}
                    </TableRow>
                    {isExpanded && getExpandedRowContent && (
                      <TableRow>
                        <TableCell
                          colSpan={row.getVisibleCells().length}
                          sx={{
                            backgroundColor: (theme) => theme.palette.grey[50],
                            p: 2,
                          }}
                        >
                          {getExpandedRowContent(row.original)}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={pageSizeOptions}
          component="div"
          count={isServerPagination ? serverPagination.totalCount : (totalRowCount ?? rows.length)}
          rowsPerPage={isServerPagination ? serverPagination.pageSize : table.getState().pagination.pageSize}
          page={isServerPagination ? serverPagination.page - 1 : table.getState().pagination.pageIndex}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          slotProps={paginationSlotProps}
          sx={{
            display: 'block',
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            boxSizing: 'border-box',
            '& .MuiTablePagination-toolbar': {
              flexWrap: 'nowrap',
              justifyContent: { xs: 'space-between', sm: 'flex-end' },
              minHeight: 52,
              px: { xs: 1, sm: 2 },
              py: 0,
            },
            '& .MuiTablePagination-spacer': {
              display: { xs: 'none', sm: 'block' },
              flex: '1 1 auto',
            },
            '& .MuiTablePagination-selectLabel': {
              display: { xs: 'none', sm: 'block' },
              m: 0,
            },
            '& .MuiTablePagination-displayedRows': {
              m: 0,
              whiteSpace: 'nowrap',
            },
            '& .MuiTablePagination-input': {
              mr: { xs: 1, sm: 4 },
              ml: { xs: 0, sm: 1 },
            },
            '& .MuiTablePagination-actions': {
              ml: { xs: 0.5, sm: 2.5 },
              '& .MuiIconButton-root': {
                p: { xs: 0.5, sm: 1 },
              },
            },
          }}
        />
      </Box>
    </Paper>
  );
};

ResizableDataGrid.propTypes = {
  rows: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  getRowId: PropTypes.func,
  disableRowSelectionOnClick: PropTypes.bool,
  sx: PropTypes.object,
  tableFrameSx: PropTypes.object,
  autoHeight: PropTypes.bool,
  initialState: PropTypes.object,
  pageSizeOptions: PropTypes.array,
  onCellClick: PropTypes.func,
  slots: PropTypes.object,
  sortModel: PropTypes.array,
  onSortModelChange: PropTypes.func,
  rowHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  getRowHeight: PropTypes.func,
  enableColumnVisibility: PropTypes.bool,
  storageKey: PropTypes.string,
  manualSorting: PropTypes.bool, // Disable client-side sorting; parent handles via onSortModelChange
  totalRowCount: PropTypes.number, // Override total count display for cursor-based pagination
  disablePaginationButtons: PropTypes.bool, // Disable page navigation buttons (prev/next)
  // Server-side pagination props
  serverPagination: PropTypes.shape({
    page: PropTypes.number.isRequired,       // 1-based page number
    pageSize: PropTypes.number.isRequired,
    totalCount: PropTypes.number.isRequired,
    onPageChange: PropTypes.func.isRequired, // (newPage: number) => void (1-based)
    onPageSizeChange: PropTypes.func.isRequired, // (newPageSize: number) => void
  }),
  // Expandable row props
  expandedRowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onRowExpand: PropTypes.func, // (rowId) => void
  getExpandedRowContent: PropTypes.func, // (row) => React.Node
  isRowExpandable: PropTypes.func, // (row) => boolean
  getRowClassName: PropTypes.func, // ({ row }) => string
  renderRowCorner: PropTypes.func, // ({ row }) => React.Node
  columnVisibilityButtonProps: PropTypes.object,
};

export default ResizableDataGrid;

